// src/pages/Home.js

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faChalkboardTeacher,
  faCameraRetro,
  faBolt,
  faGlobe,
  faUserCog,
  faShareAlt,
  faUsers,
  faUserShield,
  faMobileAlt,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/Home.css";

const Home = () => {
  const [scrollY, setScrollY] = useState(0);

  // Parallax scroll
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      setScrollY(y);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll-reveal animations
  useEffect(() => {
    const elements = document.querySelectorAll(".reveal-on-scroll");
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.18,
        rootMargin: "0px 0px -80px 0px",
      }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-container" style={{ "--scrollY": `${scrollY}px` }}>
      {/* SEO Metadata */}
      <Helmet>
        <title>GhrianGhraffy - Secure Photo &amp; Video Sharing for Irish Primary Schools</title>
        <meta
          name="description"
          content="GhrianGhraffy helps Irish Primary Schools capture, approve and share school photos & videos with parents, your website and social media — securely, with a simple principal-controlled workflow."
        />
        <meta
          name="keywords"
          content="irish primary school, school photo sharing, school video sharing, teachers photo app, secure school photos, GDPR school photos, principal approval, school website galleries"
        />
      </Helmet>

      <main>
        {/* Hero Section */}
        <header className="hero">
          <div className="hero-inner">
            <div className="hero-copy">
              <div className="hero-pill-row">
                <span className="hero-pill">Secure by design</span>
                <span className="hero-pill hero-pill-outline">
                  Built for Ireland&apos;s Primary schools
                </span>
              </div>

              <h1>School photos &amp; videos — shared safely, in minutes.</h1>

              <p className="tagline">
                GhrianGhraffy replaces scattered WhatsApp groups, USB keys and untracked photo
                folders with one secure workflow: <strong>Capture</strong>,{" "}
                <strong>Approve</strong>, <strong>Share</strong>.
              </p>

              <div className="cta-buttons">
                <Link to="/principal-registration" className="cta-button primary">
                  Get Started with Free Trial
                </Link>
                <Link to="/learn-more" className="cta-button secondary">
                  See How it Works
                </Link>
              </div>

              <div className="hero-meta">
                <div className="meta-item">
                  <FontAwesomeIcon icon={faLock} />
                  <span>Privacy-first workflow for GDPR-conscious schools</span>
                </div>
                <div className="meta-item">
                  <FontAwesomeIcon icon={faBolt} />
                  <span>Less admin: no resizing, renaming, chasing or re-uploading</span>
                </div>
              </div>
            </div>

            <div className="hero-visual">
              <div className="device-shell">
                <div className="device-header">
                  <span className="device-dot" />
                  <span className="device-dot" />
                  <span className="device-dot" />
                </div>

                <div className="device-screen">
                  <div className="device-photo-card">
                    <div className="device-photo" />
                    <div className="device-photo-meta">
                      <span className="device-photo-tag">#FirstClass</span>
                      <span className="device-photo-status">Awaiting approval</span>
                    </div>
                  </div>

                  <div className="device-photo-card secondary">
                    <div className="device-photo small" />
                    <div className="device-photo-meta">
                      <span className="device-photo-tag">#SportsDay</span>
                      <span className="device-photo-status success">
                        Approved • shared with parents
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="floating-note floating-note-top">
                <FontAwesomeIcon icon={faCameraRetro} />
                <span>New upload from Ms. O&apos;Brien</span>
              </div>

              <div className="floating-note floating-note-bottom">
                <FontAwesomeIcon icon={faShareAlt} />
                <span>Published to school website</span>
              </div>
            </div>
          </div>
        </header>

        {/* Strip / value statement */}
        <section className="section strip">
          <p>
            A principal-controlled photo space for your school: staff capture moments, a nominated
            reviewer approves them, and families view them securely — without compromising privacy
            or adding workload.
          </p>
        </section>

        {/* The problem we solve */}
        <section className="section muted" id="problems">
          <div className="section-inner">
            <div className="section-header">
              <p className="eyebrow">The problem</p>
              <h2>School photos are usually shared in ways that are messy or risky</h2>
              <p className="section-lead">
                Most schools end up using tools that were never designed for child photos —
                which creates admin effort, uncertainty and privacy headaches.
              </p>
            </div>

            <div className="feature-grid">
              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faLock} />
                </div>
                <h3>Privacy uncertainty</h3>
                <p>
                  Photos end up in personal camera rolls, group chats, or shared drives —
                  making it hard to know who has access, where they go, or how long they live.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faBolt} />
                </div>
                <h3>Time drain</h3>
                <p>
                  Uploading to multiple places means repeated work: sorting folders, renaming
                  files, resizing images, emailing links and responding to follow-up queries.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faUserShield} />
                </div>
                <h3>No clear approval workflow</h3>
                <p>
                  When content is shared ad-hoc, there&apos;s no consistent review step —
                  which increases risk and puts pressure on busy staff.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Feature Grid */}
        <section className="section" id="features">
          <div className="section-inner">
            <div className="section-header">
              <p className="eyebrow">Why GhrianGhraffy</p>
              <h2>Everything you need in one simple workflow</h2>
              <p className="section-lead">
                Capture moments once, review them in one place, and share them where you choose —
                with principal-friendly controls designed for Irish Primary Schools.
              </p>
            </div>

            <div className="feature-grid">
              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faUserShield} />
                </div>
                <h3>Secure by default</h3>
                <p>
                  Photos and videos stay within your school environment and are visible only to
                  authorised principals, staff and parents.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faMobileAlt} />
                </div>
                <h3>Built for busy teachers</h3>
                <p>
                  Teachers snap and send in seconds — no camera roll clutter, no manual organising,
                  just point, click and done.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faShareAlt} />
                </div>
                <h3>Multi-channel sharing</h3>
                <p>
                  Share approved media to GhrianGhraffy.com, your school website and Instagram from
                  one secure app — without repeating work.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faUserCog} />
                </div>
                <h3>Principal-friendly controls</h3>
                <p>
                  Approve in one click, manage staff access, and keep an overview of what&apos;s being
                  shared and where.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faChalkboardTeacher} />
                </div>
                <h3>Delight parents</h3>
                <p>
                  Parents get a simple, password-protected view of their child&apos;s class photos —
                  with no ads, noise or social feeds.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faGlobe} />
                </div>
                <h3>Works with your website</h3>
                <p>
                  A lightweight WordPress plugin makes it effortless to display school galleries on
                  your existing website.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="section muted" id="how-it-works">
          <div className="section-inner">
            <div className="section-header">
              <p className="eyebrow">How it works</p>
              <h2>From classroom to web in 3 steps</h2>
              <p className="section-lead">
                A clean, repeatable workflow that keeps control where it belongs — with the school.
              </p>
            </div>

            <div className="steps-grid">
              <div className="step-card reveal-on-scroll">
                <div className="step-number">1</div>
                <h3>Capture</h3>
                <p>
                  Staff use the GhrianGhraffy app to take photos/videos during class, sports days
                  or school events — without saving to their personal device.
                </p>
              </div>

              <div className="step-card reveal-on-scroll">
                <div className="step-number">2</div>
                <h3>Approve</h3>
                <p>
                  Principals (or nominated staff) review and approve content in a focused dashboard.
                  One click, and it&apos;s ready to share.
                </p>
              </div>

              <div className="step-card reveal-on-scroll">
                <div className="step-number">3</div>
                <h3>Share</h3>
                <p>
                  Approved media is shared with parents and, if you choose, your school website and
                  Instagram — from one place.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Role-based value props */}
        <section className="section" id="for-schools">
          <div className="section-inner">
            <div className="section-header">
              <p className="eyebrow">Made for everyone in your school</p>
              <h2>Different dashboards, same simple experience</h2>
              <p className="section-lead">
                Each role gets exactly what they need — no clutter, no confusion, no risky workarounds.
              </p>
            </div>

            <div className="audience-grid">
              <article className="audience-card reveal-on-scroll">
                <div className="audience-icon">
                  <FontAwesomeIcon icon={faUserTie} />
                </div>
                <h3>For principals</h3>
                <p>
                  Stay in control of what&apos;s shared, keep data safe and show off school life — without
                  adding another admin job to your day.
                </p>
              </article>

              <article className="audience-card reveal-on-scroll">
                <div className="audience-icon">
                  <FontAwesomeIcon icon={faChalkboardTeacher} />
                </div>
                <h3>For staff</h3>
                <p>
                  Capture and upload in the moment. No resizing, renaming or chasing permissions across
                  multiple channels.
                </p>
              </article>

              <article className="audience-card reveal-on-scroll">
                <div className="audience-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <h3>For parents</h3>
                <p>
                  Enjoy a secure, ad-free view of your child&apos;s school memories from any device, any time —
                  with a clear boundary between school content and personal social media.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Confidence / reassurance section */}
        <section className="section muted" id="trust">
          <div className="section-inner">
            <div className="section-header">
              <p className="eyebrow">Peace of mind</p>
              <h2>Designed to reduce risk, not add rules</h2>
              <p className="section-lead">
                GhrianGhraffy is built around a simple idea: make the secure option the easiest option.
              </p>
            </div>

            <div className="feature-grid">
              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faLock} />
                </div>
                <h3>Private, school-first sharing</h3>
                <p>
                  Parents view approved content in a password-protected experience — no open links, no
                  public feeds, and no “forwarded around” surprises.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faUserCog} />
                </div>
                <h3>Clear accountability</h3>
                <p>
                  A consistent approval step means everyone knows what “ready to share” looks like —
                  and principals can confidently delegate review when needed.
                </p>
              </article>

              <article className="feature-card reveal-on-scroll">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faGlobe} />
                </div>
                <h3>Your website stays yours</h3>
                <p>
                  Use your existing WordPress site and publish curated galleries without staff logging
                  into website admin panels or juggling files.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="section" id="testimonials">
          <div className="section-inner">
            <div className="section-header">
              <p className="eyebrow">What schools are saying</p>
              <h2>Loved by principals &amp; teachers</h2>
            </div>

            <div className="testimonial-grid">
              <article className="testimonial-card reveal-on-scroll">
                <p>
                  GhrianGhraffy has transformed how we share school life with families. It&apos;s now part
                  of our daily routine.
                </p>
                <h4>— Mrs. Browne, Principal</h4>
              </article>

              <article className="testimonial-card reveal-on-scroll">
                <p>
                  I can share photos from class in just a few taps. Parents feel more connected, and
                  it takes me less time than before.
                </p>
                <h4>— Ms. O&apos;Brien, Teacher</h4>
              </article>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="section final-cta">
          <div className="section-inner">
            <div className="final-cta-card reveal-on-scroll">
              <h2>Ready to see GhrianGhraffy in action?</h2>
              <p>Start a secure photo-sharing space for your school in just a few minutes.</p>
              <div className="cta-buttons">
                <Link to="/principal-registration" className="cta-button primary">
                  Get started with Free Trial
                </Link>
                <a href="mailto:information@ghrianghraffy.com" className="cta-button tertiary">
                  Talk to us
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <p>&copy; {new Date().getFullYear()} GhrianGhraffy. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/terms">Terms &amp; Conditions</Link>
            <Link to="/privacy">Privacy, Security &amp; GDPR</Link>
            <Link to="/faq">Security &amp; Privacy FAQ</Link>
            <Link to="/contact">Contact Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
